import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { getAllOwnerLedgers, getOwnerLedgerDetailItems, resetOwnerLedgerState, selectOwnerLedgerDetailItemsCurrentPage, selectOwnerLedgerState } from './sagas/OwnerLedgerSlice';
import { LedgerItemDetailDto, LedgerSummaryDto, SearchLedgerDetailsParamsDto } from '@inosio/payments-sdk';
import LedgerBody from '../../shared/ledger/components/LedgerBody';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { format, startOfYear, subDays, subYears } from 'date-fns';
import { IsoDateRange } from '../../pmui/shared/CalendarModal.V2';
import { useHistory } from 'react-router';
import { ThemeProvider } from '@mui/system';
import { mdTheme } from '../../shared/ledger/styles/LedgerStyles';
import { selectAuthState } from '../../shared/sagas/auth/authSlice';
import { selectOwnerDetails } from '../../pmui/owners/sagas/ownersSlice';

export default function OwnerLedger() {
  const dispatch = useDispatch();
  const ownerLedgerState = useSelector(selectOwnerLedgerState);
  const ledgerHistoryDataBook = useSelector(selectOwnerLedgerDetailItemsCurrentPage);
  const { ipaUser } = useSelector(selectAuthState);
  const OwnerDetails = useSelector(selectOwnerDetails);

  const history = useHistory();

  const {
    ownerLedgerDetailItemFetchResponse,
    isFetchingOwnerLedgerDetailItems,
    isFetchingOwnerLedgerDetailItemsFailed,
    ownerLedgerDetailItemFetchError,
    ownerLedgersFetchResponse,
    isFetchingOwnerLedgersSuccess,
    isFetchingOwnerLedgersFailed,
    ownerLedgersFetchError,
    isFetchingOwnerLedgers,
  } = ownerLedgerState;

  //---------States for Pagination------------------//

  const [rangeSize, setRangeSize] = useState(50); //default to 50
  const [recordStartIndex, setRecordStartIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const availableDateRanges: { Label: string; DateRange: IsoDateRange }[] = [
    {
      Label: 'Last 30 Days',
      DateRange: [format(subDays(new Date(), 30), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')],
    },
    {
      Label: 'Last 60 Days',
      DateRange: [format(subDays(new Date(), 60), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')],
    },
    {
      Label: 'Last 120 Days',
      DateRange: [format(subDays(new Date(), 120), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')],
    },
    {
      Label: 'Year to date',
      DateRange: [format(startOfYear(new Date()), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')],
    },
    {
      Label: 'All time',
      DateRange: [format(subYears(new Date(), 3), 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd')],
    },
  ];

  const [selectedDateRangeLabel, setSelectedDateRangeLabel] = useState(availableDateRanges[2].Label);
  const [selectedDateRange, setSelectedDateRange] = useState(availableDateRanges[2].DateRange);

  //-----------States for Ledger Type---------------//
  const [availableLedgers, setAvailableLedgers] = useState<LedgerSummaryDto[]>();
  const [selectedLedgerUuid, setSelectedLedgerUuid] = useState('');

  //-----------Handlers for Ledger Type Filtering---//

  const handleOnLedgerTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedLedgerUuid(event.target.value);
  };

  //-----------Handlers for Date range change---//

  const handleOnDateRangeChange = (event: SelectChangeEvent<string>) => {
    const selectedRange = availableDateRanges.find((d) => d.Label === event.target.value)!;
    setSelectedDateRangeLabel(selectedRange.Label);
    setSelectedDateRange(selectedRange.DateRange);
    console.log(event.target.value);
  };
  //-----------Handlers for pagination-------------//

  //only change the start index if we need to flag to the slice to fetch
  const setSearchPageIndex = (pageNumber: number) => {
    if (!ledgerHistoryDataBook[pageNumber]) {
      const newIndex = pageNumber * rangeSize;
      setRecordStartIndex(newIndex);
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPageNumber: number) => {
    setSearchPageIndex(newPageNumber);
    setCurrentPage(newPageNumber);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCurrentPage(0);
    setRecordStartIndex(0);
    setRangeSize(parseInt(event.target.value));
  };

  //-------------------API Calls-------------------//

  const handleSearch = (rangeSize: number, startIndex: number, dateRange?: IsoDateRange | undefined) => {
    let _searchQueryString: string = '';
    if (dateRange) {
      _searchQueryString = _searchQueryString.concat(`&startDate=${dateRange[0]}&endDate=${dateRange[1]}`);
    }

    //update the query string to represent what is being searched for
    history.push({ pathname: '/ownerui/ledger', search: _searchQueryString });

    dispatch({
      type: getOwnerLedgerDetailItems.type,
      payload: {
        ledgerUuid: selectedLedgerUuid,
        ledgerDetailsSearchParams: {
          RangeSize: rangeSize,
          StartIndex: startIndex,
          EndDate: dateRange?.[1],
          StartDate: dateRange?.[0],
          EntityOwnerUuid: ipaUser?.currentProfile.uuid,
        } as SearchLedgerDetailsParamsDto,
      },
    });
  };

  //fetch all ledgers for this owner on load
  useEffect(() => {
    dispatch(getAllOwnerLedgers(ipaUser?.currentProfile.uuid));
    return () => {
      setSelectedLedgerUuid('');
      dispatch(resetOwnerLedgerState());
    };
  }, [dispatch]);

  //once all ledgers fetched, set the local variables
  useEffect(() => {
    if (isFetchingOwnerLedgersSuccess && OwnerDetails.Uuid) {
      //find the one ledger that looks like the owner, make that first.
      setAvailableLedgers(ownerLedgersFetchResponse);
      const mostLikelyOwnersLedger = ownerLedgersFetchResponse?.find((ls) => ls.ExternalRefId1 === OwnerDetails.ExternalRefId1);
      if (mostLikelyOwnersLedger) {
        setSelectedLedgerUuid(mostLikelyOwnersLedger.LedgerUuid);
      }else{
        setSelectedLedgerUuid(ownerLedgersFetchResponse?.[0]?.LedgerUuid);
      }
    }
  }, [isFetchingOwnerLedgersSuccess, ownerLedgersFetchResponse, OwnerDetails]);

  //once all ledgers fetched, fetch the details for the selected/default ledger
  useEffect(() => {
    if (selectedLedgerUuid) {
      handleSearch(rangeSize, recordStartIndex, selectedDateRange);
    }
  }, [dispatch, rangeSize, recordStartIndex, selectedDateRange, selectedLedgerUuid]);

  return (
    <ThemeProvider theme={mdTheme}>
      <Stack id="ledger-container">
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start' }} padding={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', padding: 0.5 }}>
            <Typography id="ledger-header-text-large">Activity</Typography>
            <FormControl>
              <Select
                labelId="type-select-label"
                id="type-select"
                value={selectedLedgerUuid || ''}
                onChange={handleOnLedgerTypeChange}
                sx={{ borderRadius: '24px', height: '42px', minWidth: '160px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
              >
                {availableLedgers &&
                  availableLedgers?.map((ledger: LedgerSummaryDto) => (
                    <MenuItem value={ledger.LedgerUuid} key={ledger.LedgerUuid}>
                      {ledger.ExternalRefId2}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 2, padding: 0.5 }}>
            <Typography id="ledger-header-text-large">Date Range</Typography>
            <FormControl>
              <Select
                labelId="type-select-label"
                id="type-select"
                value={selectedDateRangeLabel}
                onChange={handleOnDateRangeChange}
                sx={{ borderRadius: '24px', height: '42px', minWidth: '160px' }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '24px',
                    },
                  },
                }}
              >
                {availableDateRanges?.map((range: { Label: string; DateRange: IsoDateRange }) => (
                  <MenuItem value={range.Label} key={range.Label}>
                    {range.Label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
        <LedgerBody
          rows={ledgerHistoryDataBook?.[currentPage]?.Rows ?? ([] as LedgerItemDetailDto[])}
          currentPage={currentPage}
          handleChangePage={handleChangePage}
          rowsPerPage={rangeSize}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          totalItems={ownerLedgerDetailItemFetchResponse.TotalCount}
          isFetchingLedgerBodyData={isFetchingOwnerLedgers || isFetchingOwnerLedgerDetailItems}
          ledgerBodyDataFetchError={ownerLedgersFetchError || ownerLedgerDetailItemFetchError}
          isFetchingLedgerBodyDataFailure={isFetchingOwnerLedgersFailed || isFetchingOwnerLedgerDetailItemsFailed}
        />
      </Stack>
    </ThemeProvider>
  );
}
